<template>
  <VApp>
    <VBtn
      variant="text"
      href="#main-content"
      class="skip-link"
      @click.prevent="focusMainContent"
    >
      {{ t('skipToMainContent') }}
    </VBtn>
    <ErrorBoundary>
      <Component
        :is="layouts[layout]"
        :options="layoutOptions"
      >
        <RouterView :key="route.path" />
      </Component>
      <AppToasts />
    </ErrorBoundary>
    <VueQueryDevtools

      v-if="env !== 'production'"
      button-position="bottom-left"
    />
  </VApp>
</template>

<script setup lang="ts">
import { VueQueryDevtools } from '@tanstack/vue-query-devtools';
import {
  ModuleRegistry,
  ClientSideRowModelModule,
  enableDevValidations,
  ClientSideRowModelApiModule,
  ExternalFilterModule,
  CheckboxEditorModule,
  TooltipModule,
  NumberEditorModule,
  CellStyleModule,
  RowStyleModule,
  QuickFilterModule,
  RenderApiModule,
  PaginationModule,
  ColumnAutoSizeModule,
  TextEditorModule,
  TextFilterModule,
  ColumnApiModule,
  RowSelectionModule,
  CustomEditorModule,
  CsvExportModule,
  EventApiModule,
} from 'ag-grid-community';
import { RowGroupingModule, RichSelectModule, SideBarModule, ColumnsToolPanelModule } from 'ag-grid-enterprise';
import { onBeforeMount, onBeforeUnmount, watch } from 'vue';
import { useHead } from '@unhead/vue';
import { getLocalStorageItem, STORAGE_KEYS } from '@/utils/localStorage';
import { useLayouts } from '@/composables/useLayouts';
import { useLocales } from '@/composables/useLocales';
import { getRouteTitle } from '@/composables/useTitleTag';
import { AppToasts } from '@teamworksdev/vuetify';
import { initGainsight, resetGainsight } from '@/plugins/gainsight';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import ErrorBoundary from '@/components/common/ErrorBoundary.vue';
import { useMainStore } from '@/stores/mainStore';
import { updateLDContext, onFlagsChange, getFlag } from '@/plugins/launchdarkly';


const env = import.meta.env.VITE_ENV;

const { t } = useI18n();
const { layout, layouts, layoutOptions } = useLayouts();
const { setLocale } = useLocales();
const routeTitle = getRouteTitle();
const route = useRoute();
const router = useRouter();
const mainStore = useMainStore();

// Re-identify the LaunchDarkly client on a team switch. The initial identify happens
// as part of bootstrapSession() in the router guard; updateLDContext()'s own isEqual
// memo prevents a redundant identify() call if the context hasn't actually changed.
// Skip when teamTwrn is cleared (logout) — there is no team context to identify with.
watch(() => mainStore.teamTwrn, (teamTwrn) => {
  if (!teamTwrn) return;
  updateLDContext().catch((error) => {
    console.warn('[LaunchDarkly] Failed to re-identify after team switch', error);
  });
});

// The router guard only checks requiredLDFlags at navigation time. If a flag is
// streamed in live (e.g. an admin disables it) while the user is already sitting on a
// route that depends on it, nothing would otherwise catch that until their next
// navigation — so react to live flag changes here and redirect immediately.
const unsubscribeFlagsChange = onFlagsChange(() => {
  const requiredLDFlags = route.meta.requiredLDFlags;
  if (requiredLDFlags?.length && requiredLDFlags.some((flag) => !getFlag(flag, false))) {
    router.push({ name: 'NoAccess' });
  }
});

const focusMainContent = () => {
  document.getElementById('main-content')?.focus();
};

useHead({
  templateParams: {
    site: { name: 'General Manager' },
    separator: '|',
  },
  titleTemplate: '%s %separator %site.name',
  title: () => routeTitle.value,
});

onBeforeMount(() => {
  setLocale(
    getLocalStorageItem<string>(STORAGE_KEYS.APP_LOCALE)
      || window.navigator.language,
  );
  initGainsight();
  if (env !== 'production') {
    enableDevValidations();
  }
  // Register ag-grid modules
  ModuleRegistry.registerModules([
    ClientSideRowModelModule,
    RowGroupingModule,
    ClientSideRowModelApiModule,
    ExternalFilterModule,
    CheckboxEditorModule,
    TooltipModule,
    NumberEditorModule,
    CellStyleModule,
    RowStyleModule,
    QuickFilterModule,
    RenderApiModule,
    PaginationModule,
    ColumnAutoSizeModule,
    TextEditorModule,
    TextFilterModule,
    ColumnApiModule,
    RowSelectionModule,
    CustomEditorModule,
    CsvExportModule,
    EventApiModule,
    RichSelectModule,
    SideBarModule,
    ColumnsToolPanelModule,
  ]);
});

onBeforeUnmount(() => {
  unsubscribeFlagsChange();
  resetGainsight();
});




</script>

<style scoped lang="scss">
.skip-link {
  position: fixed;
  top: 0;
  left: 50%;
  z-index: 9999;
  transform: translate(-50%, -100%);

  &:focus {
    transform: translate(-50%, 1rem);
  }
}
</style>
